"use client";

import { motion, AnimatePresence, Variants } from "framer-motion";
import { useState, useEffect } from "react";
import ShadowCard from "@/components/common/ShadowCard";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface GameCarouselSectionProps {
  subtitle: string;
  title: string;
  description: string;
  images: { src: string; alt: string }[];
  reversed?: boolean;
}

export default function GameCarouselSection({
  subtitle,
  title,
  description,
  images,
  reversed = false,
}: GameCarouselSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0); // 1 for right, -1 for left
  const [animationDistance, setAnimationDistance] = useState({
    gradient: 200,
    shadow: 150,
  });

  useEffect(() => {
    const updateDistance = () => {
      if (window.innerWidth >= 1024) {
        setAnimationDistance({ gradient: 800, shadow: 400 });
      } else {
        setAnimationDistance({ gradient: 200, shadow: 150 });
      }
    };
    updateDistance();
    window.addEventListener("resize", updateDistance);
    return () => window.removeEventListener("resize", updateDistance);
  }, []);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  // Responsive animation distances for initial load
  const gradientXDistance = animationDistance.gradient;
  const shadowXDistance = animationDistance.shadow;

  // Text animation variants
  const textVariants: Variants = {
    hidden: { y: 80, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
        delay: 0.1,
      },
    },
  };

  // Static background card variants
  const gradientCardVariants: Variants = {
    hidden: {
      x: reversed ? gradientXDistance : -gradientXDistance,
      opacity: 0,
      rotate: -18,
    },
    visible: {
      x: 0,
      opacity: 1,
      rotate: -18,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    },
  };

  // Image card variants for carousel transitions
  const slideVariants: Variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      rotate: -10.36,
    }),
    center: {
      x: 0,
      opacity: 1,
      rotate: -10.36,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      rotate: -10.36,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    }),
  };

  const TextContent = (
    <motion.div
      className="flex flex-col justify-center lg:max-w-[500px]"
      variants={textVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
    >
      <p className="font-raleway mb-2.5 text-base font-normal text-gray-400">
        {subtitle}
      </p>
      <h2 className="font-raleway mb-2.5 text-xl leading-tight font-bold text-white md:text-3xl md:text-[2rem]">
        {title}
      </h2>
      <p className="font-raleway mb-8 text-base leading-[1.2] text-gray-300 md:text-base">
        {description}
      </p>

      {/* Navigation Arrows */}
      <div className="flex gap-4">
        <button
          onClick={handlePrev}
          className="group border-brand-red hover:bg-brand-red flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border transition-all active:scale-95"
          aria-label="Previous image"
        >
          <ChevronLeft className="text-brand-red h-6 w-6 transition-colors group-hover:text-white" />
        </button>
        <button
          onClick={handleNext}
          className="group border-brand-red hover:bg-brand-red flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border transition-all active:scale-95"
          aria-label="Next image"
        >
          <ChevronRight className="text-brand-red h-6 w-6 transition-colors group-hover:text-white" />
        </button>
      </div>
    </motion.div>
  );

  return (
    <section className="bg-brand-black relative w-full overflow-hidden py-16 lg:py-24">
      <div
        className={`flex flex-col items-center gap-10 px-5 md:gap-20 md:px-12 lg:flex-row lg:items-center lg:gap-85 lg:px-0 ${
          reversed ? "lg:flex-row-reverse" : ""
        }`}
      >
        {/* Text Content */}
        <div
          className={`w-full lg:flex-1 ${reversed ? "lg:pr-[100px]" : "lg:pl-[100px]"}`}
        >
          {TextContent}
        </div>

        {/* Cards Stack */}
        <div className="flex w-full justify-center lg:w-auto lg:shrink-0 lg:justify-start">
          <div className="relative mt-16 mb-8 h-[318px] w-[265px] lg:my-0 lg:h-[722px] lg:w-[600px]">
            {/* Gradient Card (Background) - Static after initial load */}
            <motion.div
              variants={gradientCardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="absolute inset-0 rounded-[20px] bg-linear-to-br from-[#EE4137] to-[#A10900]"
              style={{
                width: "100%",
                height: "100%",
                transformOrigin: "center center",
              }}
            />

            {/* Shadow Card with Image (Foreground) - Carousel with AnimatePresence */}
            <div
              className={`absolute inset-0 lg:inset-auto lg:top-[27px] ${reversed ? "lg:left-0" : "lg:left-[36px]"} lg:h-[calc(100%-27px)] lg:w-[calc(100%-36px)]`}
              style={{
                transformOrigin: "center center",
              }}
            >
              <AnimatePresence initial={false} custom={direction}>
                <motion.div
                  key={currentIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  className="absolute inset-0 h-full w-full"
                >
                  <ShadowCard className="h-full w-full overflow-hidden">
                    <Image
                      src={images[currentIndex].src}
                      alt={images[currentIndex].alt}
                      fill
                      className="object-cover"
                      priority
                    />
                  </ShadowCard>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
